import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Données personnelles d'un joueur : réservées à son club et aux gestionnaires de
 * ligue. Elles se lisent par la RPC `get_players_private` (migration
 * 20261012233000) ; la révocation des colonnes à `authenticated` suit (M2). Aucune
 * lecture client de `players` ne doit les nommer, ni utiliser `*`.
 * Nationalité et groupe sanguin restent des colonnes publiques.
 */
export const PLAYER_PRIVATE_COLUMNS = [
  "nin",
  "date_of_birth",
  "place_of_birth",
  "father_name",
  "mother_name",
] as const;

/** Colonnes d'une ligne joueur de liste (forme `Player`, sans date de naissance). */
export const PLAYER_LIST_COLUMNS =
  "id, first_name, last_name, full_name, first_name_ar, last_name_ar, license_number, player_number, current_team_id, is_archived, created_at, created_by, updated_at, updated_by";

/** Colonnes des sélecteurs de joueur (effectif d'une équipe). */
export const PLAYER_PICKER_COLUMNS =
  "id, first_name, last_name, first_name_ar, last_name_ar, license_number, current_team_id, is_archived, player_number";

/** PostgREST « function not found » (PGRST202), ou Postgres undefined_function (42883). */
export function isMissingRpcError(error: { code?: string } | null | undefined): boolean {
  return error?.code === "PGRST202" || error?.code === "42883";
}

/**
 * La fiche ne peut pas s'ouvrir en édition : l'appelant ne lit pas les données
 * personnelles du joueur. L'ouvrir avec une date vide l'effacerait à
 * l'enregistrement (`savePlayer` réécrit `date_of_birth`).
 */
export class PlayerPrivateDataUnavailableError extends Error {
  readonly playerId: string;
  constructor(playerId: string) {
    super(`Données personnelles du joueur ${playerId} non accessibles`);
    this.name = "PlayerPrivateDataUnavailableError";
    this.playerId = playerId;
  }
}

/**
 * Date de naissance d'un joueur, par `get_players_private`. Tant que la RPC n'est
 * pas déployée (PGRST202 / 42883), la colonne est encore lisible : on la relit.
 * Pas de ligne = pas le droit : on lève, jamais `null`.
 */
export async function fetchPlayerBirthDate(
  supabase: SupabaseClient,
  playerId: string
): Promise<string | null> {
  const { data, error } = await supabase.rpc("get_players_private", { p_player_ids: [playerId] });
  if (error) {
    if (!isMissingRpcError(error)) throw error;
    const { data: row, error: rowError } = await supabase
      .from("players")
      .select("date_of_birth")
      .eq("id", playerId)
      .single();
    if (rowError) throw rowError;
    return (row as { date_of_birth?: string | null } | null)?.date_of_birth ?? null;
  }
  const row = ((data as Array<{ id: string; date_of_birth: string | null }> | null) ?? []).find(
    (r) => r.id === playerId
  );
  if (!row) throw new PlayerPrivateDataUnavailableError(playerId);
  return row.date_of_birth ?? null;
}

/** Clé i18n (espace `management`) d'un échec d'ouverture de fiche joueur, ou null. */
export function openPlayerErrorKey(error: unknown): string | null {
  return error instanceof PlayerPrivateDataUnavailableError
    ? "audit_route.notify_private_data_unavailable"
    : null;
}
